/**
 * useMatrixClassification Hook
 *
 * Classifies hexagons based on the current matrix/simple filter configuration
 * and applies visual styles to the map features.
 *
 * Extracted from MatrixFilterMode to isolate classification logic
 * and make it independently testable.
 *
 * @param {Object} params
 * @param {Array} params.hexagons - Array of OpenLayers hexagon features
 * @param {number} params.hexagonSize - Size of each hexagon in m²
 * @param {Object} params.filter - Current filter configuration (matrix or simple)
 * @param {Function} params.setFilterBoxes - Callback to update parent's filter boxes state
 * @returns {Object} classificationResults - Per-class results with counts, areas, and hexagon refs
 */

import { useEffect, useMemo } from 'react';

import {
	classifyWithMatrixFilter,
	classifyWithSimpleFilter,
	hexToRgba,
} from './matrixClassification';
import { classificationStyles } from '@utils/map/layer/classificationStyles';

export default function useMatrixClassification({ hexagons, hexagonSize, filter, setFilterBoxes }) {
	// Extract only the fields that affect classification to avoid re-computing
	// when display-only fields change (customPriceFields, currency, name).
	const classificationKey = useMemo(() => ({
		type: filter.type,
		classes: filter.classes,
		bands: filter.bands,
		thresholds: filter.thresholds,
		property: filter.property,
		bandProperty: filter.bandProperty,
		thresholdProperty: filter.thresholdProperty,
	}), [
		filter.type,
		filter.classes,
		filter.bands,
		filter.thresholds,
		filter.property,
		filter.bandProperty,
		filter.thresholdProperty,
	]);

	// Classify hexagons and compute results
	const classificationResults = useMemo(() => {
		const results = {};

		// Initialize results for each class
		classificationKey.classes.forEach(cls => {
			results[cls.id] = {
				...cls,
				colorObj: hexToRgba(cls.color, cls.opacity ?? 0.7),
				hexagons: [],
				count: 0,
				totalArea: 0,
				areaPercentage: 0,
			};
		});

		// Add special categories
		results.below_minimum = {
			id: 'below_minimum',
			label: 'Below Minimum',
			color: '#808080',
			colorObj: { r: 128, g: 128, b: 128, a: 0.5 },
			hexagons: [],
			count: 0,
			totalArea: 0,
			areaPercentage: 0,
		};
		results.unclassified = {
			id: 'unclassified',
			label: 'Unclassified',
			color: '#646464',
			colorObj: { r: 100, g: 100, b: 100, a: 0.3 },
			hexagons: [],
			count: 0,
			totalArea: 0,
			areaPercentage: 0,
		};

		// Classify each hexagon
		hexagons.forEach(hexagon => {
			const meta = hexagon.get('data')?.metadata;
			if (!meta) {
				results.unclassified.hexagons.push(hexagon);
				return;
			}

			// Use appropriate classification function based on filter type
			const classId =
				classificationKey.type === 'simple'
					? classifyWithSimpleFilter(meta, classificationKey)
					: classifyWithMatrixFilter(meta, classificationKey);

			if (classId && results[classId]) {
				results[classId].hexagons.push(hexagon);
			} else {
				results.unclassified.hexagons.push(hexagon);
			}
		});

		// Calculate statistics
		const totalHexagons = hexagons.length;
		Object.keys(results).forEach(classId => {
			const r = results[classId];
			r.count = r.hexagons.length;
			r.totalArea = r.count * hexagonSize;
			r.areaPercentage = totalHexagons > 0 ? ((r.count / totalHexagons) * 100).toFixed(1) : 0;
		});

		return results;
	}, [hexagons, hexagonSize, classificationKey]);

	// Apply colors to hexagons and build filterBoxes for the parent component.
	//
	// filterBoxes is consumed by:
	//   - HexagonColorFilter (parent) for the Summary component
	//   - PDFExportButton for report generation
	//
	// Each entry has the shape: { label, color, multiplier, matchedHexagons,
	//   totalArea, areaPercentage, totalCalculation }
	//
	// Special categories (below_minimum, unclassified) are appended with
	// multiplier: 0 so they appear in exports but don't affect cost totals.
	useEffect(() => {
		if (!hexagons.length) {
			setFilterBoxes([]);
			return;
		}

		Object.keys(classificationResults).forEach(classId => {
			const result = classificationResults[classId];

			result.hexagons.forEach(hexagon => {
				hexagon.setProperties({
					colors: [result.colorObj],
					name: result.label,
					description: `Class: ${result.label}`,
					matrixClass: classId,
				});
				hexagon.setStyle(classificationStyles([result.colorObj]));
			});
		});

		// Update filter boxes for summary/export
		const filterBoxes = filter.classes.map(cls => {
			const r = classificationResults[cls.id];
			return {
				label: cls.label,
				color: r.colorObj,
				multiplier: cls.multiplier || 1,
				matchedHexagons: r.count,
				totalArea: r.totalArea,
				areaPercentage: r.areaPercentage,
				totalCalculation: r.totalArea * (cls.multiplier || 1),
			};
		});

		// Include special categories when they have hexagons
		const belowMin = classificationResults.below_minimum;
		if (belowMin?.count > 0) {
			filterBoxes.push({
				label: belowMin.label,
				color: belowMin.colorObj,
				multiplier: 0,
				matchedHexagons: belowMin.count,
				totalArea: belowMin.totalArea,
				areaPercentage: belowMin.areaPercentage,
				totalCalculation: 0,
			});
		}

		const unclassified = classificationResults.unclassified;
		if (unclassified?.count > 0) {
			filterBoxes.push({
				label: unclassified.label,
				color: unclassified.colorObj,
				multiplier: 0,
				matchedHexagons: unclassified.count,
				totalArea: unclassified.totalArea,
				areaPercentage: unclassified.areaPercentage,
				totalCalculation: 0,
			});
		}

		setFilterBoxes(filterBoxes);
	}, [classificationResults, hexagons, setFilterBoxes, filter.classes]);

	return classificationResults;
}
